<section class="rel-shell">
  <header class="rel-header">
    <div>
      <p class="eyebrow">Reliability Engineering</p>
      <h1>System Health Control Room</h1>
      <p>Separate monitoring for GRMIS, Asterisk, PostgreSQL, AI, WhatsApp, Email, SMS and security events.</p>
    </div>
    <div class="header-actions">
      <button class="rel-btn" (click)="bootstrapDefaults()">Prepare Defaults</button>
      <button class="rel-btn primary" (click)="loadAll()" [disabled]="loading">
        {{ loading ? 'Refreshing...' : 'Refresh' }}
      </button>
    </div>
  </header>

  <section class="rel-bootstrap">
    <label>Default Base URL
      <input [(ngModel)]="bootstrapBaseUrl" name="bootstrapBaseUrl" placeholder="https://drl.cogentcore.ai/grms3" />
    </label>
    <label>Environment
      <input [(ngModel)]="bootstrapEnvironment" name="bootstrapEnvironment" />
    </label>
  </section>

  <div class="rel-alert error" *ngIf="error">{{ error }}</div>
  <div class="rel-toast" *ngIf="toast">{{ toast }}</div>

  <nav class="rel-tabs">
    <button [class.active]="activeTab === 'dashboard'" (click)="activeTab = 'dashboard'">Dashboard</button>
    <button [class.active]="activeTab === 'adapters'" (click)="activeTab = 'adapters'">Adapters</button>
    <button [class.active]="activeTab === 'incidents'" (click)="activeTab = 'incidents'">Incidents</button>
    <button [class.active]="activeTab === 'checks'" (click)="activeTab = 'checks'">Health Checks</button>
    <button [class.active]="activeTab === 'rules'" (click)="activeTab = 'rules'">Alert Rules</button>
    <button [class.active]="activeTab === 'runbooks'" (click)="activeTab = 'runbooks'">Runbooks</button>
  </nav>

  <ng-container *ngIf="activeTab === 'dashboard'">
    <div class="rel-metrics">
      <article class="metric">
        <span>Overall Health</span>
        <strong [class]="statusClass(dashboard?.overallStatus)">{{ dashboard?.overallStatus || 'UNKNOWN' }}</strong>
      </article>
      <article class="metric">
        <span>Configured Adapters</span>
        <strong>{{ dashboard?.adapterCount || 0 }}</strong>
      </article>
      <article class="metric">
        <span>Active Incidents</span>
        <strong [class]="dashboard?.activeIncidentCount ? 'bad' : 'ok'">{{ dashboard?.activeIncidentCount || 0 }}</strong>
      </article>
      <article class="metric">
        <span>Critical Open</span>
        <strong [class]="dashboard?.criticalOpenCount ? 'bad' : 'ok'">{{ dashboard?.criticalOpenCount || 0 }}</strong>
      </article>
      <article class="metric">
        <span>High Open</span>
        <strong [class]="dashboard?.highOpenCount ? 'bad' : 'ok'">{{ dashboard?.highOpenCount || 0 }}</strong>
      </article>
      <article class="metric">
        <span>Alert Rules</span>
        <strong>{{ dashboard?.alertRuleCount || 0 }}</strong>
      </article>
      <article class="metric">
        <span>Runbooks</span>
        <strong>{{ dashboard?.runbookCount || 0 }}</strong>
      </article>
    </div>

    <section class="rel-card">
      <div class="rel-card-head">
        <h2>Adapter Health</h2>
        <button class="rel-btn" (click)="newAdapter()">Add Adapter</button>
      </div>
      <div class="adapter-grid">
        <article class="adapter-tile" *ngFor="let adapter of dashboard?.adapterSummaries || []">
          <div>
            <b>{{ adapter.adapterName }}</b>
            <span>{{ adapter.adapterType }} · {{ adapter.environmentName }}</span>
          </div>
          <strong [class]="statusClass(adapter.lastStatus)">{{ adapter.lastStatus || 'UNKNOWN' }}</strong>
          <div class="summary-line">
            <span>{{ adapter.recentSuccessRate || 0 }}% success</span>
            <span>{{ adapter.avgLatencyMs || 0 }} ms avg</span>
            <span>{{ adapter.activeIncidentCount || 0 }} active</span>
          </div>
          <div class="summary-line">
            <span>{{ adapter.requiredCheckTargetCount || 0 }} required</span>
            <span>{{ adapter.enabledCheckTargetCount || 0 }} enabled</span>
            <span>Next {{ adapter.nextDueAt ? (adapter.nextDueAt | date:'shortTime') : '-' }}</span>
          </div>
          <strong [class]="statusClass(adapter.phase2Status)">{{ adapter.phase2Status }}</strong>
          <button class="rel-btn" (click)="runCheck(adapter)" [disabled]="testingAdapterId === adapter.adapterId">
            {{ testingAdapterId === adapter.adapterId ? 'Checking...' : 'Check' }}
          </button>
        </article>
      </div>
      <div class="planned" *ngIf="!adapters.length">
        Configure the first GRMIS adapter to begin monitoring.
      </div>
    </section>
  </ng-container>

  <ng-container *ngIf="activeTab === 'adapters'">
    <section class="rel-grid">
      <form class="rel-card form-card" (ngSubmit)="saveAdapter()">
        <h2>Adapter Configuration</h2>
        <label>Adapter Name
          <input name="adapterName" [(ngModel)]="adapterForm.adapterName" required />
        </label>
        <label>Adapter Type
          <select name="adapterType" [(ngModel)]="adapterForm.adapterType">
            <option *ngFor="let type of adapterTypes" [value]="type">{{ type }}</option>
          </select>
        </label>
        <label>Environment
          <input name="environmentName" [(ngModel)]="adapterForm.environmentName" />
        </label>
        <label>Base URL / Host
          <input name="baseUrl" [(ngModel)]="adapterForm.baseUrl" placeholder="https://drl.cogentcore.ai/grms3" />
        </label>
        <label>Health Check URL
          <input name="healthCheckUrl" [(ngModel)]="adapterForm.healthCheckUrl" placeholder="/api/v1/auth/landingPageCount" />
        </label>
        <div class="two-col">
          <label>Auth Type
            <select name="authType" [(ngModel)]="adapterForm.authType">
              <option *ngFor="let auth of authTypes" [value]="auth">{{ auth }}</option>
            </select>
          </label>
          <label>Timeout Seconds
            <input name="timeoutSeconds" type="number" min="1" max="60" [(ngModel)]="adapterForm.timeoutSeconds" />
          </label>
        </div>
        <label>Check Interval Seconds
          <input name="checkIntervalSeconds" type="number" min="60" max="86400" [(ngModel)]="adapterForm.checkIntervalSeconds" />
          <small>Scheduler runs this adapter after this interval. Allowed: 60 to 86400 seconds.</small>
        </label>
        <label *ngIf="adapterForm.authType === 'BEARER'">Bearer Token
          <input name="authToken" [(ngModel)]="adapterForm.authToken" [placeholder]="adapterForm.authTokenSet ? 'Token saved - leave blank to keep it' : ''" />
        </label>
        <label class="toggle">
          <input name="enabled" type="checkbox" [(ngModel)]="adapterForm.enabled" />
          Enabled
        </label>
        <div class="rel-actions">
          <button class="rel-btn primary" type="submit">Save Adapter</button>
          <button class="rel-btn" type="button" (click)="newAdapter(adapterForm.adapterType)">Clear</button>
        </div>
      </form>

      <section class="rel-card">
        <h2>Configured Adapters</h2>
        <div class="rel-list">
          <article *ngFor="let adapter of adapters">
            <div>
              <b>{{ adapter.adapterName }}</b>
              <span>{{ adapter.adapterType }} · {{ adapter.baseUrl || 'No base URL' }}</span>
            </div>
            <strong [class]="statusClass(adapter.lastStatus)">{{ adapter.lastStatus || 'UNKNOWN' }}</strong>
            <button class="rel-btn" (click)="editAdapter(adapter)">Edit</button>
            <button class="rel-btn" (click)="selectAdapterTargets(adapter)">Targets</button>
            <button class="rel-btn" (click)="runCheck(adapter)">Check</button>
            <button class="rel-btn danger" (click)="deleteAdapter(adapter)">Remove</button>
          </article>
        </div>
      </section>
    </section>

    <section class="rel-card" *ngIf="selectedAdapterForTargets">
      <div class="rel-card-head">
        <div>
          <h2>Check Targets: {{ selectedAdapterForTargets.adapterName }}</h2>
          <p class="hint">Only these visible targets are executed. Adapter Type is classification only; it does not add hidden endpoints.</p>
        </div>
        <button class="rel-btn" type="button" (click)="newCheckTarget()">New Target</button>
      </div>

      <form class="target-form" (ngSubmit)="saveCheckTarget()">
        <label>Check Name
          <input name="targetCheckName" [(ngModel)]="checkTargetForm.checkName" required />
        </label>
        <label>Mode
          <select name="targetCheckMode" [(ngModel)]="checkTargetForm.checkMode">
            <option *ngFor="let mode of checkModes" [value]="mode">{{ mode }}</option>
          </select>
        </label>
        <label>URL / Command
          <input name="targetUrlOrCommand" [(ngModel)]="checkTargetForm.urlOrCommand" placeholder="/health or http://127.0.0.1:18282/whatsapp/health" />
        </label>
        <label>Method
          <select name="targetHttpMethod" [(ngModel)]="checkTargetForm.httpMethod">
            <option *ngFor="let method of httpMethods" [value]="method">{{ method }}</option>
          </select>
        </label>
        <label>Expected Status
          <input name="targetExpectedStatusCodes" [(ngModel)]="checkTargetForm.expectedStatusCodes" placeholder="200-299,304" />
        </label>
        <label>Timeout Seconds
          <input name="targetTimeoutSeconds" type="number" min="1" max="60" [(ngModel)]="checkTargetForm.timeoutSeconds" />
        </label>
        <label>Auth
          <select name="targetAuthType" [(ngModel)]="checkTargetForm.authType">
            <option *ngFor="let auth of authTypes" [value]="auth">{{ auth }}</option>
          </select>
        </label>
        <label *ngIf="checkTargetForm.authType === 'BEARER'">Bearer Token
          <input name="targetAuthToken" [(ngModel)]="checkTargetForm.authToken" [placeholder]="checkTargetForm.authTokenSet ? 'Token saved - leave blank to keep it' : ''" />
        </label>
        <label class="toggle">
          <input name="targetRequired" type="checkbox" [(ngModel)]="checkTargetForm.required" />
          Required
        </label>
        <label class="toggle">
          <input name="targetEnabled" type="checkbox" [(ngModel)]="checkTargetForm.enabled" />
          Enabled
        </label>
        <button class="rel-btn primary" type="submit">Save Target</button>
      </form>

      <div class="target-table">
        <div class="target-row head">
          <span>Name</span><span>Mode</span><span>URL / Command</span><span>Required</span><span>Auth</span><span>Latest</span><span>Actions</span>
        </div>
        <div class="target-row" *ngFor="let target of checkTargets">
          <span>
            <b>{{ target.checkName }}</b>
            <small>{{ target.enabled ? 'Enabled' : 'Disabled' }} · {{ target.timeoutSeconds || 5 }}s</small>
          </span>
          <span>{{ target.checkMode }} {{ target.checkMode === 'HTTP' ? target.httpMethod : '' }}</span>
          <span class="mono">{{ target.urlOrCommand || '-' }}</span>
          <span [class]="target.required ? 'bad' : 'muted'">{{ target.required ? 'Required' : 'Optional' }}</span>
          <span>{{ target.authType || 'NONE' }} {{ target.authTokenSet ? '(secret set)' : '' }}</span>
          <span>
            <b [class]="statusClass(target.latestStatus)">{{ target.latestStatus || 'Not checked' }}</b>
            <small>{{ target.latestCheckedAt ? (target.latestCheckedAt | date:'short') : '-' }}</small>
          </span>
          <span class="row-actions">
            <button class="rel-btn" type="button" (click)="editCheckTarget(target)">Edit</button>
            <button class="rel-btn" type="button" (click)="runCheckTarget(target)" [disabled]="testingCheckTargetId === target.checkTargetId">
              {{ testingCheckTargetId === target.checkTargetId ? 'Checking...' : 'Run' }}
            </button>
            <button class="rel-btn danger" type="button" (click)="deleteCheckTarget(target)">Remove</button>
          </span>
        </div>
        <div class="empty" *ngIf="!checkTargets.length">No check targets configured for this adapter.</div>
      </div>
    </section>
  </ng-container>

  <ng-container *ngIf="activeTab === 'incidents'">
    <section class="rel-card">
      <div class="rel-card-head">
        <h2>Incidents</h2>
        <div class="incident-filters">
          <select [(ngModel)]="incidentFilter.status" name="filterStatus" (ngModelChange)="loadIncidents()">
            <option value="">All statuses</option>
            <option *ngFor="let status of incidentStatuses" [value]="status">{{ status }}</option>
          </select>
          <select [(ngModel)]="incidentFilter.severity" name="filterSeverity" (ngModelChange)="loadIncidents()">
            <option value="">All severities</option>
            <option *ngFor="let severity of severities" [value]="severity">{{ severity }}</option>
          </select>
          <select [(ngModel)]="incidentFilter.adapterType" name="filterAdapterType" (ngModelChange)="loadIncidents()">
            <option value="">All systems</option>
            <option *ngFor="let type of adapterTypes" [value]="type">{{ type }}</option>
          </select>
        </div>
      </div>
      <div class="rel-table">
        <div class="row head">
          <span>Severity</span><span>Status</span><span>System</span><span>Incident</span><span>SLA</span><span>Actions</span>
        </div>
        <div class="row" *ngFor="let incident of incidents">
          <span [class]="statusClass(incident.severity)">{{ incident.severity }}</span>
          <span [class]="statusClass(incident.status)">{{ incident.status }}</span>
          <span>{{ incident.adapterName }}</span>
          <span>
            <b>{{ incident.title }}</b>
            <small>{{ incident.rootCauseHint }}</small>
          </span>
          <span>
            <b [class]="incident.slaBreached ? 'bad' : 'ok'">{{ incident.slaBreached ? 'Breached' : 'Within SLA' }}</b>
            <small>{{ incident.ageMinutes }}m / {{ incident.slaTargetMinutes }}m</small>
          </span>
          <span class="row-actions">
            <button class="rel-btn" (click)="openIncident(incident)">View</button>
            <button class="rel-btn" (click)="acknowledge(incident)" [disabled]="incident.status === 'RESOLVED'">Ack</button>
            <button class="rel-btn primary" (click)="resolve(incident)" [disabled]="incident.status === 'RESOLVED'">Resolve</button>
          </span>
        </div>
        <div class="empty" *ngIf="!incidents.length">No incidents yet.</div>
      </div>
    </section>
  </ng-container>

  <ng-container *ngIf="activeTab === 'checks'">
    <section class="rel-card">
      <h2>Recent Health Checks</h2>
      <div class="rel-table checks">
        <div class="row head">
          <span>Status</span><span>Adapter / Check</span><span>Mode</span><span>HTTP</span><span>Latency</span><span>Resolved URL</span><span>Checked At</span><span>Error</span>
        </div>
        <div class="row" *ngFor="let check of healthChecks">
          <span [class]="statusClass(check.status)">{{ check.status }}</span>
          <span>
            <b>{{ check.checkName }}</b>
            <small>{{ check.adapterName }} · {{ check.requiredTarget ? 'Required' : 'Optional' }}</small>
          </span>
          <span>{{ check.checkMode || '-' }}</span>
          <span>{{ check.httpStatusCode || '-' }}</span>
          <span>{{ check.responseTimeMs || 0 }} ms</span>
          <span class="mono">{{ check.resolvedUrl || '-' }}</span>
          <span>{{ check.checkedAt | date:'short' }}</span>
          <span>{{ check.errorMessage || '-' }}</span>
        </div>
        <div class="empty" *ngIf="!healthChecks.length">No health checks have been run.</div>
      </div>
    </section>
  </ng-container>

  <ng-container *ngIf="activeTab === 'rules'">
    <section class="rel-grid">
      <form class="rel-card form-card" (ngSubmit)="saveAlertRule()">
        <h2>Alert Rule</h2>
        <label>Adapter Type
          <select name="ruleAdapterType" [(ngModel)]="alertRuleForm.adapterType" (ngModelChange)="alertRuleForm.checkType = $event">
            <option *ngFor="let type of adapterTypes" [value]="type">{{ type }}</option>
          </select>
        </label>
        <label>Check Type
          <input name="checkType" [(ngModel)]="alertRuleForm.checkType" />
        </label>
        <div class="two-col">
          <label>Severity
            <select name="severity" [(ngModel)]="alertRuleForm.severity">
              <option *ngFor="let severity of severities" [value]="severity">{{ severity }}</option>
            </select>
          </label>
          <label>Notify
            <input name="notificationChannel" [(ngModel)]="alertRuleForm.notificationChannel" />
          </label>
        </div>
        <div class="two-col">
          <label>Failure Threshold
            <input name="failureThreshold" type="number" min="1" [(ngModel)]="alertRuleForm.failureThreshold" />
          </label>
          <label>Window Minutes
            <input name="timeWindowMinutes" type="number" min="1" [(ngModel)]="alertRuleForm.timeWindowMinutes" />
          </label>
        </div>
        <label class="toggle">
          <input name="ruleEnabled" type="checkbox" [(ngModel)]="alertRuleForm.enabled" />
          Enabled
        </label>
        <div class="rel-actions">
          <button class="rel-btn primary" type="submit">Save Rule</button>
          <button class="rel-btn" type="button" (click)="newAlertRule(alertRuleForm.adapterType)">Clear</button>
        </div>
      </form>

      <section class="rel-card">
        <h2>Configured Rules</h2>
        <div class="rel-list">
          <article *ngFor="let rule of alertRules">
            <div>
              <b>{{ rule.adapterType }} · {{ rule.checkType }}</b>
              <span>{{ rule.failureThreshold }} failures in {{ rule.timeWindowMinutes }} minutes → {{ rule.severity }}</span>
            </div>
            <strong [class]="rule.enabled ? 'ok' : 'muted'">{{ rule.enabled ? 'Enabled' : 'Disabled' }}</strong>
            <button class="rel-btn" (click)="editAlertRule(rule)">Edit</button>
            <button class="rel-btn danger" (click)="deleteAlertRule(rule)">Remove</button>
          </article>
          <div class="empty" *ngIf="!alertRules.length">No alert rules configured. Default behavior is 3 failures in 5 minutes.</div>
        </div>
      </section>
    </section>
  </ng-container>

  <ng-container *ngIf="activeTab === 'runbooks'">
    <section class="rel-grid">
      <form class="rel-card form-card" (ngSubmit)="saveRunbook()">
        <h2>Runbook</h2>
        <label>Adapter Type
          <select name="runbookAdapterType" [(ngModel)]="runbookForm.adapterType" (ngModelChange)="runbookForm.incidentType = $event + '_HEALTH_FAILURE'">
            <option *ngFor="let type of adapterTypes" [value]="type">{{ type }}</option>
          </select>
        </label>
        <label>Incident Type
          <input name="incidentType" [(ngModel)]="runbookForm.incidentType" />
        </label>
        <label>Title
          <input name="runbookTitle" [(ngModel)]="runbookForm.title" />
        </label>
        <label>Description
          <textarea name="description" rows="3" [(ngModel)]="runbookForm.description"></textarea>
        </label>
        <label>Steps
          <textarea name="steps" rows="7" [(ngModel)]="runbookForm.steps"></textarea>
        </label>
        <div class="two-col">
          <label>Severity
            <select name="runbookSeverity" [(ngModel)]="runbookForm.severity">
              <option *ngFor="let severity of severities" [value]="severity">{{ severity }}</option>
            </select>
          </label>
          <label class="toggle runbook-toggle">
            <input name="runbookEnabled" type="checkbox" [(ngModel)]="runbookForm.enabled" />
            Enabled
          </label>
        </div>
        <div class="rel-actions">
          <button class="rel-btn primary" type="submit">Save Runbook</button>
          <button class="rel-btn" type="button" (click)="newRunbook(runbookForm.adapterType)">Clear</button>
        </div>
      </form>

      <section class="rel-card">
        <h2>Runbook Library</h2>
        <div class="runbook-list">
          <article *ngFor="let runbook of runbooks">
            <header>
              <div>
                <b>{{ runbook.title }}</b>
                <span>{{ runbook.adapterType }} · {{ runbook.incidentType }}</span>
              </div>
              <button class="rel-btn" (click)="editRunbook(runbook)">Edit</button>
              <button class="rel-btn danger" (click)="deleteRunbook(runbook)">Remove</button>
            </header>
            <p>{{ runbook.description || 'No description yet.' }}</p>
            <pre>{{ runbook.steps }}</pre>
          </article>
          <div class="empty" *ngIf="!runbooks.length">No runbooks configured.</div>
        </div>
      </section>
    </section>
  </ng-container>

  <div class="incident-modal" *ngIf="selectedIncident">
    <section class="incident-panel">
      <header>
        <div>
          <p class="eyebrow">{{ selectedIncident.adapterType }} Incident</p>
          <h2>{{ selectedIncident.title }}</h2>
          <span [class]="statusClass(selectedIncident.status)">{{ selectedIncident.status }}</span>
        </div>
        <div class="header-actions">
          <button class="rel-btn" (click)="generateIncidentReport()">Generate Report</button>
          <button class="rel-btn" (click)="closeIncident()">Close</button>
        </div>
      </header>
      <div class="incident-summary">
        <article>
          <b>Business Impact</b>
          <p>{{ selectedIncident.businessImpact || '-' }}</p>
        </article>
        <article>
          <b>Technical Summary</b>
          <p>{{ selectedIncident.technicalSummary || '-' }}</p>
        </article>
        <article>
          <b>Root Cause Hint</b>
          <p>{{ selectedIncident.rootCauseHint || '-' }}</p>
        </article>
        <article>
          <b>Owner</b>
          <p>{{ selectedIncident.assignedTo || 'Unassigned' }}</p>
        </article>
        <article>
          <b>SLA</b>
          <p [class]="selectedIncident.slaBreached ? 'bad' : 'ok'">
            {{ selectedIncident.slaBreached ? 'Breached' : 'Within SLA' }}
            · {{ selectedIncident.ageMinutes }}m / {{ selectedIncident.slaTargetMinutes }}m
          </p>
        </article>
      </div>
      <section class="incident-workflow">
        <label>Assign To
          <input [(ngModel)]="incidentAssignee" name="incidentAssignee" placeholder="Officer / team name" />
        </label>
        <button class="rel-btn" (click)="assignSelectedIncident()">Assign</button>
        <label>Comment
          <textarea [(ngModel)]="incidentComment" name="incidentComment" rows="2" placeholder="Add investigation note or recovery update"></textarea>
        </label>
        <button class="rel-btn" (click)="addSelectedIncidentComment()">Add Comment</button>
      </section>
      <h3>Timeline</h3>
      <div class="timeline">
        <article *ngFor="let event of selectedIncident.events">
          <strong>{{ event.eventType }}</strong>
          <span>{{ event.createdAt | date:'medium' }}</span>
          <p>{{ event.message }}</p>
          <small>{{ event.eventPayload }}</small>
        </article>
      </div>
      <h3>Recent Checks</h3>
      <div class="rel-table checks compact">
        <div class="row head">
          <span>Status</span><span>Check</span><span>HTTP</span><span>Latency</span><span>Checked At</span><span>Error</span>
        </div>
        <div class="row" *ngFor="let check of selectedIncident.recentChecks">
          <span [class]="statusClass(check.status)">{{ check.status }}</span>
          <span>
            <b>{{ check.checkName }}</b>
            <small>{{ check.adapterName }}</small>
          </span>
          <span>{{ check.httpStatusCode || '-' }}</span>
          <span>{{ check.responseTimeMs || 0 }} ms</span>
          <span>{{ check.checkedAt | date:'short' }}</span>
          <span>{{ check.errorMessage || '-' }}</span>
        </div>
      </div>

      <section class="report-panel" *ngIf="incidentReport">
        <h3>{{ incidentReport.title }}</h3>
        <p class="report-meta">Generated at {{ incidentReport.generatedAt | date:'medium' }}</p>
        <h4>Summary</h4>
        <p>{{ incidentReport.summary }}</p>
        <h4>Timeline</h4>
        <pre>{{ incidentReport.timeline }}</pre>
        <h4>Recent Checks</h4>
        <pre>{{ incidentReport.recentChecks }}</pre>
        <h4>Recommendations</h4>
        <pre>{{ incidentReport.recommendations }}</pre>
      </section>
    </section>
  </div>
</section>
